import React, { useState, useEffect, useRef } from 'react';
import { 
  X, 
  Check, 
  Filter,
  RotateCcw,
  Save,
  Settings
} from 'lucide-react';
import { TRIP_ID } from '../../constants/tripIds';

interface TripFilterPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (selectedTripIds: string[]) => void;
  initialSelectedTripIds?: string[];
}

export const TripFilterPanel: React.FC<TripFilterPanelProps> = ({
  isOpen,
  onClose,
  onSave,
  initialSelectedTripIds = []
}) => {
  const [selectedTripIds, setSelectedTripIds] = useState<string[]>(initialSelectedTripIds);
  const [autoApply, setAutoApply] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  const panelRef = useRef<HTMLDivElement>(null);
  const firstCheckboxRef = useRef<HTMLInputElement>(null);

  // Focus management and keyboard navigation
  useEffect(() => {
    if (isOpen) {
      // Focus first checkbox when opened
      setTimeout(() => {
        firstCheckboxRef.current?.focus();
      }, 100);

      // Handle escape key
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen, onClose]);

  // Reset state when panel opens
  useEffect(() => {
    if (isOpen) {
      setSelectedTripIds(initialSelectedTripIds);
      setHasChanges(false);
    }
  }, [isOpen, initialSelectedTripIds]);

  // Auto-apply with debounce
  useEffect(() => {
    if (autoApply && hasChanges) {
      const timeoutId = setTimeout(() => {
        onSave(selectedTripIds);
        setHasChanges(false);
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [selectedTripIds, autoApply, hasChanges, onSave]);

  const handleTripToggle = (tripId: string) => {
    setSelectedTripIds(prev => {
      const newSelection = prev.includes(tripId)
        ? prev.filter(id => id !== tripId)
        : [...prev, tripId];
      
      setHasChanges(true);
      return newSelection;
    });
  };

  const handleSelectAll = () => {
    const allTripIds = Object.values(TRIP_ID);
    setSelectedTripIds(allTripIds);
    setHasChanges(true);
  };

  const handleClearAll = () => {
    setSelectedTripIds([]);
    setHasChanges(true);
  };

  const handleSave = () => {
    onSave(selectedTripIds);
    setHasChanges(false);
    onClose();
  };

  const getTripName = (tripId: string) => {
    const entry = Object.entries(TRIP_ID).find(([_, id]) => id === tripId);
    return entry ? entry[0].replace(/_/g, ' ') : tripId;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-[60] p-4">
      <div 
        ref={panelRef}
        className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden"
        role="dialog"
        aria-labelledby="filter-panel-title"
        aria-describedby="filter-panel-description"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Filter className="h-5 w-5 text-primary-600" />
            <h2 id="filter-panel-title" className="text-lg font-semibold text-gray-900">
              Filter by Trips
            </h2>
            {selectedTripIds.length > 0 && (
              <span className="px-2 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                {selectedTripIds.length}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
            aria-label="Close filter panel"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          <p id="filter-panel-description" className="text-sm text-gray-600">
            Select trips to filter conversations. Leave empty to show all chats.
          </p>

          {/* Auto-apply toggle */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <Settings className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Apply instantly</span>
            </div>
            <button
              onClick={() => setAutoApply(!autoApply)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                autoApply ? 'bg-primary-600' : 'bg-gray-200'
              }`}
              aria-label={`${autoApply ? 'Disable' : 'Enable'} auto-apply`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  autoApply ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Action buttons */}
          <div className="flex space-x-2">
            <button
              onClick={handleSelectAll}
              className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Check className="h-4 w-4" />
              <span>Select All</span>
            </button>
            <button
              onClick={handleClearAll}
              className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <RotateCcw className="h-4 w-4" />
              <span>Clear All</span>
            </button>
          </div>

          {/* Trip list */}
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {Object.entries(TRIP_ID).map(([tripName, tripId], index) => (
              <label
                key={tripId}
                className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer"
              >
                <input
                  ref={index === 0 ? firstCheckboxRef : undefined}
                  type="checkbox"
                  checked={selectedTripIds.includes(tripId)}
                  onChange={() => handleTripToggle(tripId)}
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  aria-label={`Select ${getTripName(tripId)} trip`}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900">
                    {getTripName(tripId)}
                  </div>
                  <div className="text-xs text-gray-500 font-mono">
                    {tripId}
                  </div>
                </div>
              </label>
            ))}
          </div>

          {/* Selection summary */}
          {selectedTripIds.length > 0 && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="text-sm text-blue-800">
                <strong>{selectedTripIds.length}</strong> trip{selectedTripIds.length !== 1 ? 's' : ''} selected
              </div>
              {selectedTripIds.length > 10 && (
                <div className="text-xs text-blue-600 mt-1">
                  Large selection may impact performance
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges && !autoApply}
            className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="h-4 w-4" />
            <span>Save</span>
          </button>
        </div>
      </div>
    </div>
  );
};
